import { buildQueryString, request } from "../client"
import type { WorkflowSummaryModel } from "@kontextwerk/web-sdk/core/layout/workflowSummary"

export const INBOX_CASE_REQUIREMENT_ASSIGN_WORK_ORDER = "ASSIGN_OR_CREATE_WORK_ORDER"
export const INBOX_CASE_REQUIREMENT_ASSIGN_REQUESTER_CUSTOMER = "ASSIGN_REQUESTER_CUSTOMER"
export const INBOX_CASE_REQUIREMENT_PLAN_APPOINTMENT = "PLAN_APPOINTMENT"
export const INBOX_CASE_REQUIREMENT_INFORM_CUSTOMER = "INFORM_CUSTOMER"
export const INBOX_CASE_REQUIREMENT_VERIFY_CUSTOMER = "VERIFY_CUSTOMER"
export const INBOX_CASE_REQUIREMENT_REVIEW_CONTACT_POINT = "REVIEW_CONTACT_POINT"
export const INBOX_CASE_REQUIREMENT_UPDATE_SOURCE_CONTACT_DATA = "UPDATE_SOURCE_CONTACT_DATA"
export const INBOX_CASE_REQUIREMENT_REPLACE_OR_REMOVE_WRONG_RECIPIENT_CUSTOMER =
  "REPLACE_OR_REMOVE_WRONG_RECIPIENT_CUSTOMER"
export const INBOX_CASE_REQUIREMENT_SELECT_APPOINTMENT_LETTERS_FOR_DELIVERY =
  "SELECT_APPOINTMENT_LETTERS_FOR_DELIVERY"
export const INBOX_CASE_REQUIREMENT_DECIDE_APPOINTMENT_LETTER_DELIVERY =
  "DECIDE_APPOINTMENT_LETTER_DELIVERY"
export const INBOX_CASE_REQUIREMENT_DELIVER_APPOINTMENT_LETTERS = "DELIVER_APPOINTMENT_LETTERS"

export type InboxActionCase = {
  id: string
  organization_id: string
  subject_case_id: string | null
  idempotency_key: string
  flow_key: string
  status: "open" | "in_progress" | "resolved" | "canceled" | string
  priority: string
  source?: string | null
  channel?: string | null
  mode?: string | null
  title: string
  summary?: string | null
  requirements: string[]
  resolved_requirements: string[]
  step_outputs: Record<string, unknown>
  payload: Record<string, unknown>
  due_at?: string | null
  closed_at?: string | null
  closed_by_user_id?: string | null
  closure_actor_kind?: "USER" | "SYSTEM" | "UNKNOWN" | null
  created_at: string
  updated_at: string
}

export type InboxWorkflowActionCase = Omit<InboxActionCase, "step_outputs">

export type InboxCaseStepDefinition = {
  step_key: string
  label: string
  component_key: string
  requirements_resolved_by_step: string[]
  batch_mode: "batch" | "single" | "mixed" | string
  output_key?: string | null
  additional_output_keys?: string[]
  optional: boolean
}

export type InboxCaseFlowDefinition = {
  flow_key: string
  label: string
  batch_mode: "batch" | "single" | "mixed" | string
  requirements: string[]
  steps: InboxCaseStepDefinition[]
  subject_policy: "REQUIRED" | "OPTIONAL" | "FORBIDDEN"
  allowed_subject_case_types: string[]
}

export type InboxCaseWorkflowStep = {
  step_key: string
  label: string
  component_key: string
  requirements_resolved_by_step: string[]
  batch_mode: "batch" | "single" | "mixed" | string
  output_key?: string | null
  additional_output_keys?: string[]
  status: "pending" | "active" | "resolved" | "skipped" | string
  output: Record<string, unknown>
}

export type InboxCaseWorkflowItemStep = {
  step_key: string
  status: "pending" | "active" | "resolved" | "skipped" | string
  output: Record<string, unknown>
}

export type InboxCaseWorkflowResponse = {
  action_case: InboxWorkflowActionCase
  flow: InboxCaseFlowDefinition
  steps: InboxCaseWorkflowItemStep[]
  context: Record<string, unknown>
  result_summary?: WorkflowSummaryModel | null
  active_step_key?: string | null
  unresolved_requirements: string[]
  is_resolved: boolean
}

export type InboxCaseBatchWorkflowRequest = {
  flow_key: string
  action_case_ids: string[]
}

export type InboxCaseBatchWorkflowItem = {
  action_case: InboxWorkflowActionCase
  steps: InboxCaseWorkflowItemStep[]
  context: Record<string, unknown>
  active_step_key?: string | null
  unresolved_requirements: string[]
  compatible: boolean
  error?: string | null
}

export type InboxCaseBatchWorkflow = {
  flow: InboxCaseFlowDefinition
  context?: Record<string, unknown>
  result_summary?: WorkflowSummaryModel | null
  items: InboxCaseBatchWorkflowItem[]
  incompatible_count: number
  can_run_batch: boolean
}

export type InboxCaseBatchActionItem = {
  action_case_id: string
  payload?: Record<string, unknown>
  client_row_id?: string | null
}

export type InboxCaseBatchActionRowResult = {
  action_case_id: string
  status: "success" | "skipped" | "failed" | string
  action_case?: InboxWorkflowActionCase | null
  detail?: string | null
  error?: string | null
  output: Record<string, unknown>
}

export type InboxCaseBatchActionResult = {
  flow_key?: string | null
  step_key: string
  rows: InboxCaseBatchActionRowResult[]
  success_count: number
  skipped_count: number
  failed_count: number
}

export type InboxCase = InboxActionCase & {
  work_order_id?: string | null
  schedule_event_id?: string | null
}

export type InboxCaseWorkflowCase = InboxWorkflowActionCase & {
  work_order_id?: string | null
  schedule_event_id?: string | null
}

export type InboxCaseActionDescriptor = {
  action: string
  label: string
  description: string
  requirement?: string | null
  priority: "primary" | "secondary" | string
  destructive: boolean
  implemented: boolean
}

export type InboxCaseContactPointValue = {
  contact_point_id?: string | null
  kind: "EMAIL" | "MOBILE" | "FIXED_LINE" | "ADDRESS" | string
  value: string
  label?: string | null
  lifecycle_status?: string | null
  is_verified: boolean
}

export type InboxCaseContactComparison = {
  existing_email?: InboxCaseContactPointValue | null
  existing_phone?: InboxCaseContactPointValue | null
  submitted_email?: InboxCaseContactPointValue | null
  submitted_phone?: InboxCaseContactPointValue | null
}

export type InboxCaseWorkflowContext = {
  title: string
  problem: string
  recommendation: string
  address_label?: string | null
  customer_label?: string | null
  contact_email?: string | null
  contact_phone?: string | null
  work_order_label?: string | null
  property_unit_label?: string | null
  schedule_start_at?: string | null
  schedule_end_at?: string | null
  schedule_label?: string | null
  source_label?: string | null
  contact_comparison?: InboxCaseContactComparison | null
  payload: Record<string, unknown>
}

export type InboxCaseWorkflow = InboxCaseWorkflowResponse & {
  case: InboxCaseWorkflowCase
  context: InboxCaseWorkflowContext
  available_actions: InboxCaseActionDescriptor[]
  primary_action?: InboxCaseActionDescriptor | null
  secondary_actions: InboxCaseActionDescriptor[]
}

export type InboxCaseQueryOptions = {
  organizationId?: string | null
  signal?: AbortSignal
}

const asObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const stringValue = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null

const firstString = (...values: unknown[]): string | null => {
  for (const value of values) {
    const normalized = stringValue(value)

    if (normalized) return normalized
  }

  return null
}

const mergePayloadContext = (
  actionCase: Pick<InboxActionCase, "payload">,
  context: Record<string, unknown> = {},
) => ({
  ...asObject(actionCase.payload),
  ...asObject(context),
})

const buildActionCase = (
  actionCase: InboxWorkflowActionCase,
  context: Record<string, unknown> = {},
): InboxCaseWorkflowCase => {
  const merged = mergePayloadContext(actionCase, context)

  return {
    ...actionCase,
    work_order_id: firstString(merged.work_order_id),
    schedule_event_id: firstString(merged.schedule_event_id),
  }
}

const buildContactComparison = (
  merged: Record<string, unknown>,
): InboxCaseContactComparison | null => {
  const comparison = asObject(merged.contact_comparison)

  return Object.keys(comparison).length ? (comparison as InboxCaseContactComparison) : null
}

const buildWorkflowContext = (
  workflow: InboxCaseWorkflowResponse,
  actionCase: InboxCaseWorkflowCase,
): InboxCaseWorkflowContext => {
  const merged = mergePayloadContext(workflow.action_case, workflow.context)

  return {
    title: actionCase.title,
    problem: firstString(merged.problem, actionCase.summary) || "",
    recommendation: firstString(merged.recommendation) || "",
    address_label: firstString(
      merged.address_label,
      merged.address,
      merged.property_label,
      merged.property_address,
    ),
    customer_label: firstString(merged.customer_label, merged.customer_name, merged.display_name),
    contact_email: firstString(merged.contact_email, merged.email),
    contact_phone: firstString(merged.contact_phone, merged.phone),
    work_order_label: firstString(merged.work_order_label, merged.order_label),
    property_unit_label: firstString(merged.property_unit_label),
    schedule_start_at: firstString(
      merged.schedule_start_at,
      merged.slot_start_at,
      merged.slot_start,
      asObject(merged.slot).start_at,
    ),
    schedule_end_at: firstString(
      merged.schedule_end_at,
      merged.slot_end_at,
      merged.slot_end,
      asObject(merged.slot).end_at,
    ),
    schedule_label: firstString(merged.schedule_label),
    source_label: firstString(merged.source_label),
    contact_comparison: buildContactComparison(merged),
    payload: merged,
  }
}

const action = (
  action: string,
  label: string,
  description: string,
  requirement: string | null,
  priority: "primary" | "secondary" = "primary",
  destructive = false,
): InboxCaseActionDescriptor => ({
  action,
  label,
  description,
  requirement,
  priority,
  destructive,
  implemented: true,
})

const buildActions = (workflow: InboxCaseWorkflowResponse): InboxCaseActionDescriptor[] => {
  const unresolved = new Set(workflow.unresolved_requirements)
  const actions: InboxCaseActionDescriptor[] = []

  if (unresolved.has(INBOX_CASE_REQUIREMENT_VERIFY_CUSTOMER)) {
    actions.push(
      action(
        "SKIP_CUSTOMER_LINK",
        "Ansprechpartner nicht hinzufügen",
        "Schließt die Ansprechpartner-Prüfung ohne neue Zuordnung ab.",
        INBOX_CASE_REQUIREMENT_VERIFY_CUSTOMER,
        "secondary",
      ),
    )
  }

  if (unresolved.has(INBOX_CASE_REQUIREMENT_UPDATE_SOURCE_CONTACT_DATA)) {
    actions.push(
      action(
        "CONFIRM_SOURCE_CONTACT_CLEANUP",
        "Kehrbezirksprogramm nachgetragen",
        "Markiert das Nachtragen im Kehrbezirksprogramm als erledigt.",
        INBOX_CASE_REQUIREMENT_UPDATE_SOURCE_CONTACT_DATA,
      ),
    )
  }

  if (unresolved.has(INBOX_CASE_REQUIREMENT_REVIEW_CONTACT_POINT)) {
    actions.push(
      action(
        "ACCEPT_CONTACT_POINT",
        "Kontaktwert übernehmen",
        "Übernimmt den gemeldeten Kontaktwert.",
        INBOX_CASE_REQUIREMENT_REVIEW_CONTACT_POINT,
      ),
      action(
        "REJECT_CONTACT_POINT",
        "Kontaktwert ignorieren",
        "Ignoriert den gemeldeten Kontaktwert.",
        INBOX_CASE_REQUIREMENT_REVIEW_CONTACT_POINT,
        "secondary",
      ),
      action(
        "REPLACE_CONTACT_POINT",
        "Alten Kontaktwert ersetzen",
        "Ersetzt den bestehenden Kontaktwert.",
        INBOX_CASE_REQUIREMENT_REVIEW_CONTACT_POINT,
      ),
    )
  }

  if (unresolved.has(INBOX_CASE_REQUIREMENT_REPLACE_OR_REMOVE_WRONG_RECIPIENT_CUSTOMER)) {
    actions.push(
      action(
        "REPLACE_OR_REMOVE_WRONG_RECIPIENT_CUSTOMER",
        "Falsche Zuordnung korrigieren",
        "Ersetzt oder entfernt die falsche Ansprechpartner-Zuordnung.",
        INBOX_CASE_REQUIREMENT_REPLACE_OR_REMOVE_WRONG_RECIPIENT_CUSTOMER,
        "primary",
      ),
    )
  }

  return actions
}

const enrichWorkflow = (workflow: InboxCaseWorkflowResponse): InboxCaseWorkflow => {
  const actionCase = buildActionCase(workflow.action_case, workflow.context)
  const actions = buildActions(workflow)

  return {
    ...workflow,
    case: actionCase,
    context: buildWorkflowContext(workflow, actionCase),
    available_actions: actions,
    primary_action: actions.find((item) => item.priority === "primary") ?? null,
    secondary_actions: actions.filter((item) => item.priority !== "primary"),
  }
}

export const fetchInboxCaseFlows = (options: InboxCaseQueryOptions = {}) =>
  request<InboxCaseFlowDefinition[]>(
    `/inbox-cases/flows${buildQueryString({
      organization_id: options.organizationId,
    })}`,
  )

export const fetchInboxCaseWorkflowResponse = (
  caseId: string,
  options: InboxCaseQueryOptions = {},
) =>
  request<InboxCaseWorkflowResponse>(
    `/inbox-cases/${caseId}/workflow${buildQueryString({
      organization_id: options.organizationId,
    })}`,
  )

export const fetchInboxCaseWorkflow = async (caseId: string, options: InboxCaseQueryOptions = {}) =>
  enrichWorkflow(await fetchInboxCaseWorkflowResponse(caseId, options))

export const fetchInboxCase = fetchInboxCaseWorkflow

export const fetchInboxCaseBatchWorkflowState = (
  payload: InboxCaseBatchWorkflowRequest,
  options: InboxCaseQueryOptions = {},
) =>
  request<InboxCaseBatchWorkflow>(
    `/inbox-cases/batch/workflow-state${buildQueryString({
      organization_id: options.organizationId,
    })}`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  )

export type InboxStepPreviewRequest = {
  action_case_ids: string[]
  payload?: Record<string, unknown>
}

export type InboxStepPreviewResult = {
  flow_key: string
  step_key: string
  preview_kind: string
  payload: Record<string, unknown>
}

export type InboxStepCommitRequest = {
  items: InboxCaseBatchActionItem[]
}

export const fetchInboxCaseStepPreview = (
  flowKey: string,
  stepKey: string,
  payload: InboxStepPreviewRequest,
  options: InboxCaseQueryOptions = {},
) =>
  request<InboxStepPreviewResult>(
    `/inbox-cases/${flowKey}/steps/${stepKey}/preview${buildQueryString({
      organization_id: options.organizationId,
    })}`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    },
  )

export const commitInboxCaseStep = (
  flowKey: string,
  stepKey: string,
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) =>
  request<InboxCaseBatchActionResult>(
    `/inbox-cases/${flowKey}/steps/${stepKey}/commit${buildQueryString({
      organization_id: options.organizationId,
    })}`,
    {
      method: "POST",
      body: JSON.stringify({ items: payload.items }),
    },
  )

export const commitCustomerContactVerificationVerifyCustomerStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) => commitInboxCaseStep("customer_contact_verification", "verify_customer", payload, options)

export const commitCustomerContactVerificationSourceCleanupStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) => commitInboxCaseStep("customer_contact_verification", "source_cleanup", payload, options)

export const commitCustomerContactPointReviewSourceCleanupStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) => commitInboxCaseStep("customer_contact_point_review", "source_cleanup", payload, options)

export const commitWrongRecipientSourceCleanupStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) => commitInboxCaseStep("wrong_recipient_replacement", "source_cleanup", payload, options)

export const commitCustomerContactPointReviewStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) => commitInboxCaseStep("customer_contact_point_review", "review_contact_points", payload, options)

export const commitWrongRecipientReplacementStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) => commitInboxCaseStep("wrong_recipient_replacement", "replace_wrong_recipient", payload, options)

export const commitUnclearAppointmentFeedbackAssignWorkOrderStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) => commitInboxCaseStep("unclear_appointment_feedback", "assign_work_order", payload, options)

export const commitUnclearAppointmentFeedbackAssignRequesterCustomerStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) =>
  commitInboxCaseStep("unclear_appointment_feedback", "assign_requester_customer", payload, options)

export const commitUnclearAppointmentFeedbackPlanStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) => commitInboxCaseStep("unclear_appointment_feedback", "plan_appointment", payload, options)

export const commitAppointmentLetterDeliveryDecisionStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) =>
  commitInboxCaseStep(
    "letter_delivery_batch",
    "decide_appointment_letter_delivery",
    payload,
    options,
  )

export const commitAppointmentLetterSelectionStep = (
  payload: InboxStepCommitRequest,
  options: InboxCaseQueryOptions = {},
) =>
  commitInboxCaseStep(
    "letter_delivery_batch",
    "select_appointment_letters_for_delivery",
    payload,
    options,
  )
